import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('kronentipp_token'));
    const [loading, setLoading] = useState(true);
    const initializedRef = useRef(false);

    const api = useCallback(() => {
        const currentToken = localStorage.getItem('kronentipp_token');
        return axios.create({
            baseURL: API_URL,
            headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {}
        });
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            const storedToken = localStorage.getItem('kronentipp_token');
            if (!storedToken) {
                setLoading(false);
                return;
            }

            try {
                const response = await axios.get(`${API_URL}/auth/me`, {
                    headers: { Authorization: `Bearer ${storedToken}` }
                });
                setUser(response.data);
                setToken(storedToken);
            } catch (error) {
                console.error('Failed to fetch user:', error);
                localStorage.removeItem('kronentipp_token');
                setToken(null);
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        if (!initializedRef.current) {
            initializedRef.current = true;
            fetchUser();
        }
    }, []);

    const login = async (email, password) => {
        const response = await axios.post(`${API_URL}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('kronentipp_token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const register = async (email, password, firstName, lastName, pseudonym = null) => {
        const response = await axios.post(`${API_URL}/auth/register`, { 
            email, 
            password, 
            first_name: firstName,
            last_name: lastName,
            pseudonym: pseudonym
        });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('kronentipp_token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const setupFirstAdmin = async (email, password, name) => {
        const response = await axios.post(`${API_URL}/setup/first-admin`, { email, password, name });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('kronentipp_token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('kronentipp_token');
        setToken(null);
        setUser(null);
    };

    const refreshUser = async () => {
        const storedToken = localStorage.getItem('kronentipp_token');
        if (storedToken) {
            try {
                const response = await axios.get(`${API_URL}/auth/me`, {
                    headers: { Authorization: `Bearer ${storedToken}` }
                });
                setUser(response.data);
            } catch (error) {
                console.error('Failed to refresh user:', error);
            }
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            token,
            loading,
            login,
            register,
            setupFirstAdmin,
            logout,
            refreshUser,
            api,
            isAdmin: user?.is_admin || false
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
